import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  DataGrid,
  gridPageCountSelector,
  gridPageSelector,
  useGridApiContext,
  useGridSelector,
} from "@mui/x-data-grid";
import Pagination from "@mui/material/Pagination";
import { Box } from "@mui/material";
import classes from "./DashboardItemDetails.module.css";
import getLandedDashboardDetailsSummaryData from "../../API/landedDashboardDetailsSummaryDataApi";

const ITEM_TYPE_TITLES = {
  REPRICENEWSKU: "Pricing for new SKUs",
  ESCALATED: "Escalated SKUs",
  ONHOLD: "On hold SKUs",
  PRIORITY: "Priority SKUs",
  OTHER: "Other SKUs",
  LANDNEWSKU: "Landing for new SKUs",
};

const DETAIL_COLUMNS = [
  { field: "batchId", headerName: "Batch ID", flex: 1.1, minWidth: 150 },
  { field: "parsedSku", headerName: "Parsed SKU", flex: 1, minWidth: 140 },
  { field: "hqLine", headerName: "HQ Line", flex: 0.8, minWidth: 120 },
  { field: "fieldLine", headerName: "Field Line", flex: 0.9, minWidth: 140 },
  { field: "oldLandedCost", headerName: "Old Landed Cost", flex: 1, minWidth: 150 },
  { field: "newLandedCost", headerName: "New Landed Cost", flex: 1, minWidth: 150 },
  { field: "newLandedCostCopy", headerName: "New Landed Cost (Copy)", flex: 1, minWidth: 160 },
  { field: "percentChgLandedCost", headerName: "% Change Landed Cost", flex: 1.2, minWidth: 180 },
  { field: "asgVendorAccountNumber", headerName: "ASG Vendor Account #", flex: 1.2, minWidth: 200 },
  { field: "asgVendorName", headerName: "ASG Vendor Name", flex: 1.3, minWidth: 220 },
  { field: "asgVendorGroupCode", headerName: "ASG Vendor Group Code", flex: 1, minWidth: 180 },
  { field: "asgVendorProductNumber", headerName: "ASG Vendor Product #", flex: 1.1, minWidth: 190 },
  { field: "asgPrimaryVendorAccountNumber", headerName: "ASG Primary Vendor Account #", flex: 1.4, minWidth: 230 },
  { field: "productLifeCycleStateName", headerName: "Lifecycle State", flex: 1, minWidth: 160 },
  { field: "countryOfOrigin", headerName: "Country of Origin", flex: 0.9, minWidth: 150 },
  { field: "fobCostUnit", headerName: "FOB Cost Unit", flex: 0.9, minWidth: 140 },
  { field: "unitCostDate", headerName: "Unit Cost Date", flex: 1, minWidth: 160 },
  { field: "oldFobCost", headerName: "Old FOB Cost", flex: 1, minWidth: 140 },
  { field: "newFobCost", headerName: "New FOB Cost", flex: 1, minWidth: 140 },
  { field: "tariffAmt", headerName: "Tariff Amount", flex: 1, minWidth: 140 },
  { field: "freightAmt", headerName: "Freight Amount", flex: 1, minWidth: 140 },
  { field: "issCommissionAmt", headerName: "ISS Commission Amount", flex: 1.1, minWidth: 180 },
  { field: "landedCostVariability", headerName: "Landed Cost Variability", flex: 1.2, minWidth: 200 },
  { field: "variabilityPercent", headerName: "Variability %", flex: 1, minWidth: 140 },
  { field: "adjustmentsRate", headerName: "Antidumping Rate", flex: 1, minWidth: 150 },
  { field: "couterVailingRate", headerName: "Countervailing Rate", flex: 1.2, minWidth: 180 },
  { field: "adjustmentsAmt", headerName: "Antidumping Amount", flex: 1.2, minWidth: 180 },
  { field: "couterVailingAmt", headerName: "Countervailing Amount", flex: 1.2, minWidth: 190 },
  { field: "containerPalletAmt", headerName: "Container Pallet Amount", flex: 1.3, minWidth: 210 },
  { field: "poStatusValue", headerName: "PO Status", flex: 0.9, minWidth: 140 },
  { field: "poProductNumber", headerName: "PO Product Number", flex: 1.2, minWidth: 190 },
  { field: "areParamsMissing", headerName: "Params Missing", flex: 1, minWidth: 150 },
  { field: "approvedDate", headerName: "Approved Date", flex: 1, minWidth: 160 },
  { field: "approvedBy", headerName: "Approved By", flex: 1, minWidth: 150 },
  { field: "nextLcCalcDate", headerName: "Next LC Calc Date", flex: 1.2, minWidth: 180 },
  { field: "repricingStatus", headerName: "Repricing Status", flex: 1, minWidth: 160 },
  { field: "eventType", headerName: "Event Type", flex: 0.9, minWidth: 140 },
  { field: "createDate", headerName: "Created Date", flex: 1, minWidth: 170 },
  { field: "createdBy", headerName: "Created By", flex: 1, minWidth: 140 },
  { field: "updateDate", headerName: "Updated Date", flex: 1, minWidth: 170 },
  { field: "updateBy", headerName: "Updated By", flex: 1, minWidth: 140 },
  { field: "approved", headerName: "Approved Status", flex: 0.8, minWidth: 140 },
  { field: "lcCalculated", headerName: "LC Calculated", flex: 0.9, minWidth: 140 },
  { field: "new", headerName: "New", flex: 0.6, minWidth: 100 },
];

const pickValue = (row, keys, fallback) => {
  for (const key of keys) {
    const value = row?.[key];
    if (value !== undefined && value !== null && value !== "") {
      return value;
    }
  }
  return fallback;
};

const formatDateParts = (value) => {
  if (Array.isArray(value)) {
    const [year, month, day, hour, minute, second] = value;
    if (year && month && day) {
      const datePart = `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
      if (hour !== undefined) {
        return `${datePart} ${String(hour ?? 0).padStart(2, "0")}:${String(minute ?? 0).padStart(2, "0")}:${String(second ?? 0).padStart(2, "0")}`;
      }
      return datePart;
    }
  }

  if (value instanceof Date) {
    return value.toISOString().slice(0, 19).replace("T", " ");
  }

  if (value === 0) {
    return "0";
  }

  return value ?? "";
};

const formatCellValue = (value) => {
  if (value === null || value === undefined || value === "") {
    return "-";
  }

  if (typeof value === "object") {
    return JSON.stringify(value);
  }

  return String(value);
};

const normalizeRow = (row, index, itemType) => ({
  id: pickValue(row, ["id", "rowId", "row_id", "uuid", "workflowId", "workflow_id"], `${itemType}-${index + 1}`),
  batchId: pickValue(row, ["batchId", "batch_id", "batchid"], ""),
  parsedSku: pickValue(row, ["parsedSku", "sku", "parsed_sku"], ""),
  hqLine: pickValue(row, ["hqLine", "hq_line"], ""),
  fieldLine: pickValue(row, ["fieldLine", "field_line"], ""),
  oldLandedCost: pickValue(row, ["oldLandedCost", "old_landed_cost"], null),
  newLandedCost: pickValue(row, ["newLandedCost", "new_landed_cost"], null),
  newLandedCostCopy: pickValue(row, ["newLandedCostCopy", "new_landed_cost_copy", "newLandedCost", "new_landed_cost"], null),
  percentChgLandedCost: pickValue(row, ["percentChgLandedCost", "percent_chg_landed_cost"], null),
  asgVendorAccountNumber: pickValue(row, ["asgVendorAccountNumber", "asg_vendor_account_number"], ""),
  asgVendorName: pickValue(row, ["asgVendorName", "asg_vendor_name"], ""),
  asgVendorGroupCode: pickValue(row, ["asgVendorGroupCode", "asg_vendor_group_code"], ""),
  asgVendorProductNumber: pickValue(row, ["asgVendorProductNumber", "asg_vendor_product_number"], ""),
  asgPrimaryVendorAccountNumber: pickValue(row, ["asgPrimaryVendorAccountNumber", "asg_primary_vendor_account_number"], ""),
  productLifeCycleStateName: pickValue(row, ["productLifeCycleStateName", "product_life_cycle_state_name"], ""),
  countryOfOrigin: pickValue(row, ["countryOfOrigin", "country_of_origin"], ""),
  fobCostUnit: pickValue(row, ["fobCostUnit", "fob_cost_unit"], ""),
  unitCostDate: formatDateParts(pickValue(row, ["unitCostDate", "unit_cost_date"], "")),
  oldFobCost: pickValue(row, ["oldFobCost", "old_fob_cost"], null),
  newFobCost: pickValue(row, ["newFobCost", "new_fob_cost"], null),
  tariffAmt: pickValue(row, ["tariffAmt", "tariff_amt"], null),
  freightAmt: pickValue(row, ["freightAmt", "freight_amt"], null),
  issCommissionAmt: pickValue(row, ["issCommissionAmt", "iss_commission_amt"], null),
  landedCostVariability: pickValue(row, ["landedCostVariability", "landed_cost_variability"], null),
  variabilityPercent: pickValue(row, ["variabilityPercent", "variability_percent"], null),
  adjustmentsRate: pickValue(row, ["adjustmentsRate", "adjustments_rate"], null),
  couterVailingRate: pickValue(row, ["couterVailingRate", "couter_vailing_rate"], null),
  adjustmentsAmt: pickValue(row, ["adjustmentsAmt", "adjustments_amt"], null),
  couterVailingAmt: pickValue(row, ["couterVailingAmt", "couter_vailing_amt"], null),
  containerPalletAmt: pickValue(row, ["containerPalletAmt", "container_pallet_amt"], null),
  poStatusValue: pickValue(row, ["poStatusValue", "po_status_value"], ""),
  poProductNumber: pickValue(row, ["poProductNumber", "po_product_number"], ""),
  areParamsMissing: pickValue(row, ["areParamsMissing", "are_params_missing", "paramMissing", "param_missing", "parammissing"], null),
  approvedDate: formatDateParts(pickValue(row, ["approvedDate", "approved_date"], "")),
  approvedBy: pickValue(row, ["approvedBy", "approved_by"], ""),
  nextLcCalcDate: formatDateParts(pickValue(row, ["nextLcCalcDate", "next_lc_calc_date"], "")),
  repricingStatus: pickValue(row, ["repricingStatus", "repricing_status"], ""),
  eventType: pickValue(row, ["eventType", "event_type"], ""),
  createDate: formatDateParts(pickValue(row, ["createDate", "create_date", "createdDate", "created_at"], "")),
  createdBy: pickValue(row, ["createdBy", "created_by"], ""),
  updateDate: formatDateParts(pickValue(row, ["updateDate", "update_date", "updatedDate", "updated_at"], "")),
  updateBy: pickValue(row, ["updateBy", "update_by"], ""),
  approved: pickValue(row, ["approved", "isApproved"], ""),
  lcCalculated: pickValue(row, ["lcCalculated", "lc_calculated"], ""),
  new: pickValue(row, ["new"], ""),
});

const normalizeRows = (rows, itemType) => rows.map((row, index) => normalizeRow(row, index, itemType));

const DashboardItemDetails = () => {
  const navigate = useNavigate();
  const { itemType } = useParams();
  const tableTitle = ITEM_TYPE_TITLES[itemType] ?? "Count of SKUs";
  const [rows, setRows] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  const CustomPagination = () => {
    const apiRef = useGridApiContext();
    const page = useGridSelector(apiRef, gridPageSelector);
    const pageCount = useGridSelector(apiRef, gridPageCountSelector);

    return (
      <Pagination
        color="primary"
        count={pageCount}
        page={page + 1}
        onChange={(_, value) => apiRef.current.setPage(value - 1)}
      />
    );
  };

  useEffect(() => {
    let isActive = true;

    const loadRows = async () => {
      setIsLoading(true);
      const response = await getLandedDashboardDetailsSummaryData(itemType);

      if (!isActive) {
        return;
      }

      setRows(Array.isArray(response) ? normalizeRows(response, itemType) : []);
      setIsLoading(false);
    };

    loadRows();

    return () => {
      isActive = false;
    };
  }, [itemType]);

  const displayRows = useMemo(() => rows, [rows]);

  return (
    <section className={classes.page}>
      <div className={classes.controls}>
        <button
          type="button"
          className={classes.backButton}
          onClick={() => navigate("/dashboard", { state: { restoreDashboardData: true } })}
        >
          Back to Dashboard
        </button>
      </div>

      <div className={classes.tableHeader}>
        <h2 className={classes.sectionTitle}>{tableTitle}</h2>
      </div>

      <Box className={classes.tableWrapper}>
        <DataGrid
          rows={displayRows}
          columns={DETAIL_COLUMNS.map((column) => ({
            ...column,
            valueFormatter: (params) => formatCellValue(params?.value),
          }))}
          autoHeight
          pagination
          loading={isLoading}
          disableRowSelectionOnClick
          hideFooterSelectedRowCount
          pageSize={15}
          rowsPerPageOptions={[15]}
          components={{ Pagination: CustomPagination }}
          getRowId={(row) => row.id}
          sx={{
            border: 0,
            fontSize: 12,
            color: "#00124c",
            "& .MuiDataGrid-columnHeaderTitle": {
              fontWeight: 600,
              color: "#5c6f94",
              textTransform: "uppercase",
              fontSize: 11,
            },
            "& .MuiDataGrid-columnHeaders": {
              backgroundColor: "#f9fbff",
              borderBottom: "1px solid #e0e6f0",
            },
            "& .MuiDataGrid-cell": {
              borderBottom: "1px solid #e0e6f0",
            },
            "& .MuiDataGrid-row:hover": {
              backgroundColor: "rgba(0, 20, 137, 0.04)",
            },
          }}
        />
      </Box>
    </section>
  );
};

export default DashboardItemDetails;
